import { useEffect, useRef, useState } from "react";
import { Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { triggerBlobDownload, type PdfVystup } from "@/lib/pdfDownload";

export interface PdfNahladDialogProps {
  /** Načítaný výstup; `null` = ešte sa generuje. */
  vystup: PdfVystup | null;
  /** Titulok — názov výstupu („Cenová ponuka", „Kalkulačný zoznam"…). */
  nazov: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Náhľad PDF pred uložením — vidíš, čo sa uloží, a vieš to rovno vytlačiť.
 *
 * Blob sa ťahá axiosom (`lib/pdfDownload.ts`), takže `Content-Disposition:
 * attachment` z backendu sa neuplatní a ten istý bajtový obsah vieme ukázať
 * aj uložiť. Blob URL je same-origin, preto `contentWindow.print()` funguje.
 */
export function PdfNahladDialog({
  vystup,
  nazov,
  open,
  onOpenChange,
}: PdfNahladDialogProps): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [url, setUrl] = useState<string | null>(null);

  // Blob URL drží browser, kým ho nezrušíme — bez revoke by pri každom
  // otvorení náhľadu ostalo v pamäti celé PDF až do zatvorenia karty.
  useEffect(() => {
    if (!vystup) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(vystup.blob);
    setUrl(u);
    return () => {
      URL.revokeObjectURL(u);
    };
  }, [vystup]);

  function tlac(): void {
    // Chrome tlačí PDF cez vlastný viewer v iframe, takže print() otvorí
    // systémový dialóg. Vo Firefoxe to na PDF iframe niekedy neurobí nič —
    // preto fallback na otvorenie v novej karte, kde je viewer plnohodnotný.
    const w = iframeRef.current?.contentWindow;
    if (w) {
      w.focus();
      w.print();
      return;
    }
    if (url) window.open(url, "_blank", "noopener");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[94vh] w-[96vw] max-w-[1240px]">
        <DialogHeader>
          <DialogTitle>{nazov}</DialogTitle>
          <DialogDescription>
            {vystup
              ? `Náhľad pred uložením — ${vystup.filename}`
              : "Generuje sa…"}
          </DialogDescription>
        </DialogHeader>

        {url ? (
          <iframe
            ref={iframeRef}
            src={url}
            title={`Náhľad ${nazov}`}
            className="h-[74vh] w-full rounded border bg-background-alt"
          />
        ) : (
          <div className="flex h-[74vh] w-full items-center justify-center rounded border bg-background-alt text-sm text-muted-foreground">
            Generuje sa…
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Zavrieť
          </Button>
          <Button
            variant="outline"
            disabled={!vystup}
            onClick={tlac}
            title="Otvorí tlačový dialóg prehliadača"
          >
            <Printer className="h-4 w-4" />
            Tlačiť
          </Button>
          <Button
            disabled={!vystup}
            onClick={() => {
              if (vystup) triggerBlobDownload(vystup.blob, vystup.filename);
            }}
            title="Uložiť PDF do počítača"
          >
            <Download className="h-4 w-4" />
            Stiahnuť
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
